<template>
  <div class="relative" ref="wrapper">
    <button
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="open"
      class="flex items-center justify-between gap-2 h-10 border border-border rounded-xl bg-[#fafafa] text-[13px] text-ink pl-3.5 pr-2.5 cursor-pointer outline-none transition-[border-color,background] duration-150 w-full hover:border-border-hover"
      :class="{ '!border-primary !bg-white': open }"
      @click="toggle"
      @keydown.escape="close"
      @keydown.down.prevent="openAndFocus"
    >
      <span :class="{ 'text-muted': !modelValue }">{{ displayLabel }}</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted shrink-0 transition-transform duration-150" :class="{ 'rotate-180': open }"><polyline points="6 9 12 15 18 9"></polyline></svg>
    </button>

    <transition name="dropdown">
      <div v-if="open" role="listbox" class="absolute top-full left-0 right-0 mt-1.5 bg-white border border-border rounded-[16px] shadow-[0_8px_24px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] z-50 py-1.5 max-h-[240px] overflow-y-auto">
        <button
          v-for="option in allOptions"
          :key="option.value"
          type="button"
          role="option"
          :aria-selected="option.value === modelValue"
          class="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left border-none bg-transparent cursor-pointer transition-colors duration-100 hover:bg-canvas"
          :class="{
            'text-primary font-semibold': option.value === modelValue,
            'text-ink': option.value !== modelValue
          }"
          @click="select(option.value)"
        >
          <span class="flex-1">{{ option.label }}</span>
          <svg v-if="option.value === modelValue" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-primary shrink-0"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'BaseSelect',
  props: {
    modelValue: { type: [String, Number, Boolean], default: '' },
    options: { type: Array, required: true },
    placeholder: { type: String, default: 'Select...' },
    valueKey: { type: String, default: 'value' },
    labelKey: { type: String, default: 'label' }
  },
  // Vue 2 uses 'value' prop and 'input' event for v-model
  model: {
    prop: 'modelValue',
    event: 'input'
  },
  data () {
    return {
      open: false
    }
  },
  computed: {
    allOptions () {
      return [
        { value: '', label: this.placeholder },
        ...this.options.map(opt => {
          if (typeof opt === 'object') {
            return { value: opt[this.valueKey], label: opt[this.labelKey] }
          }
          return { value: opt, label: opt }
        })
      ]
    },
    displayLabel () {
      if (!this.modelValue) return this.placeholder
      const found = this.allOptions.find(o => o.value === this.modelValue)
      return found ? found.label : this.modelValue
    }
  },
  methods: {
    toggle () {
      this.open = !this.open
    },
    close () {
      this.open = false
    },
    openAndFocus () {
      this.open = true
    },
    select (value) {
      this.$emit('input', value)
      this.open = false
    },
    onClickOutside (e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.close()
      }
    },
    onEscape (e) {
      if (e.key === 'Escape' && this.open) {
        e.stopPropagation()
        this.close()
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.onClickOutside)
    document.addEventListener('keydown', this.onEscape)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onClickOutside)
    document.removeEventListener('keydown', this.onEscape)
  }
}
</script>

<style>
.dropdown-enter-active {
  transition: all 0.15s ease;
}

.dropdown-leave-active {
  transition: all 0.1s ease;
}

.dropdown-enter {
  opacity: 0;
  transform: translateY(-4px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
